import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useCardContext } from '../contexts/CardContext';
import { CardStateService } from '../services/cardStateService';
import { CardState } from '../types/cardTypes';
import { StatusPageLayout } from '../components/layout/StatusPageLayout';

const CardRouterPage: React.FC = () => {
  const { cardId } = useParams<{ cardId: string }>();
  const { cardData, isLoading, error, fetchCardData, setCardId } = useCardContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (!cardId) {
      navigate('/error-page?cardId=No card ID provided');
      return;
    }
    setCardId(cardId);
    fetchCardData(cardId);
  }, [cardId, fetchCardData, navigate, setCardId]);

  useEffect(() => {
    if (!cardData || !cardId) return;

    if (cardData.state === CardState.NEW && searchParams.get('fromNewCard') === 'true') {
      const retryTimeout = setTimeout(() => {
        fetchCardData(cardId);
      }, 2000);
      return () => clearTimeout(retryTimeout);
    }
  }, [cardData, cardId, searchParams, fetchCardData]);

  useEffect(() => {
    if (cardData?.state === CardState.NEW && searchParams.get('fromNewCard') === 'true') {
      return;
    }

    if (cardData && cardId && !isLoading && !hasRedirectedRef.current) {
      if (cardData.state === CardState.KYCPENDING) {
        const sessionKey = `mcc_kyc_pending_seen_${cardId}`;
        const seen = sessionStorage.getItem(sessionKey);
        if (seen) {
          const targetRoute = `/kyc-in-process?cardId=${encodeURIComponent(cardId)}`;
          // #region agent log
          fetch('http://127.0.0.1:7361/ingest/ac35d597-8d02-4005-931f-a2b834303e25',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'55344c'},body:JSON.stringify({sessionId:'55344c',location:'CardRouter.tsx:kyc-pending-session',message:'KYCPENDING with session flag redirect',data:{cardId,cardState:cardData.state,targetRoute,currentPath:window.location.pathname},timestamp:Date.now(),hypothesisId:'B'})}).catch(()=>{});
          // #endregion
          if (window.location.pathname + window.location.search !== targetRoute) {
            hasRedirectedRef.current = true;
            navigate(targetRoute);
          }
          return;
        }
      }

      const targetRoute = CardStateService.getRouteForCardState(cardData.state, cardId);
      const currentPath = window.location.pathname;
      // #region agent log
      fetch('http://127.0.0.1:7361/ingest/ac35d597-8d02-4005-931f-a2b834303e25',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'55344c'},body:JSON.stringify({sessionId:'55344c',location:'CardRouter.tsx:state-redirect',message:'CardRouter redirect decision',data:{cardId,cardState:cardData.state,targetRoute,currentPath,willNavigate:currentPath!==targetRoute},timestamp:Date.now(),hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      if (currentPath !== targetRoute) {
        hasRedirectedRef.current = true;
        navigate(targetRoute);
      }
    }
  }, [cardData, cardId, isLoading, navigate, searchParams]);

  if (!cardId) {
    return (
      <StatusPageLayout
        variant="loading"
        title="Redirecting"
        body="No card ID provided. Redirecting to error page."
      />
    );
  }

  if (isLoading) {
    return (
      <StatusPageLayout
        variant="loading"
        title="Loading your card"
        body={`Fetching card data for ${cardId}…`}
      />
    );
  }

  if (error) {
    return (
      <StatusPageLayout
        variant="error"
        title="Could not load card"
        body={error}
      />
    );
  }

  return (
    <StatusPageLayout
      variant="loading"
      title="Redirecting"
      body={`Taking you to the right step for card ${cardId} (${cardData?.state || 'unknown'}).`}
    />
  );
};

export default CardRouterPage;
